/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { TFunction } from 'react-i18next';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { Button } from 'app/components/atoms/button/button';
import { Link } from 'react-router-dom';
import React from 'react';

/**
 * `MissingTranslation` type.
 */

type MissingTranslation = {
  id: string;
  translationKey: string;
  version: string;
  errorCode: string | null;
  context: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<MissingTranslation> => [
  {
    title: translate('common.labels.translationKey'),
    dataIndex: 'translationKey',
    size: '1fr',
    key: 'translationKey',
    render: item => <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{item.translationKey}</span>
  },
  {
    title: translate('common.labels.version'),
    dataIndex: 'version',
    key: 'version',
    render: item => <span style={{ fontSize: '13px' }}>{item.version}</span>
  },
  {
    title: translate('common.labels.errorCode'),
    dataIndex: 'errorCode',
    key: 'errorCode',
    render: item => <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{item.errorCode || '-'}</span>
  },
  {
    title: translate('common.labels.context'),
    dataIndex: 'context',
    key: 'context',
    render: item => (
      <span style={{ fontSize: '12px', color: '#666' }}>
        {item.context ? (item.context.length > 50 ? `${item.context.substring(0, 50)}...` : item.context) : '-'}
      </span>
    )
  },
  {
    title: translate('common.labels.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} showMilliseconds />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search'),
    style: { flexBasis: '100%', marginBottom: '8px' }
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<MissingTranslation> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.developer.missingTranslations,
    enableExport: true,
    canEdit: false,
    canRemove: false,
    resolveParams: (params: any, item?: MissingTranslation) => {
      return {
        id: params.id ?? item?.id
      };
    },
    key: ['missing-translations'],
    defaultQueryString: 'page=1&pageSize=50',
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters || {};

      return {
        filters: pickBy(filters, identity),
        order: data.order && data.order.length > 0 ? data.order : [{ key: 'createdAt', direction: 'desc' }]
      };
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 50,
      pageSizeOptions: [10, 25, 50, 100, 250],
      pagingByTime: true,
      timeField: 'createdAt'
    },
    endpoint: apiEndpoints.missingTranslations,
    endpointById: apiEndpoints.missingTranslationsById,
    renderActions: (item: MissingTranslation) => {
      const addTranslationUrl = `${appRoutes.dashboard.developer.translations}/add?translationKey=${encodeURIComponent(
        item.translationKey
      )}&version=${encodeURIComponent(item.version)}&languageCode=pt`;

      return (
        <Link to={addTranslationUrl}>
          <Button size={'small'} type={'primary'}>
            {translate('common.actions.add')}
          </Button>
        </Link>
      );
    }
  };
}

/**
 * Config.
 */

export function createMissingTranslationsTemplate(translate: TFunction): CrudTemplate<MissingTranslation> {
  return {
    list: list(translate)
  };
}
